import React, { useState, useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';
import './App.css';
import MainContainer from './components/MainContainer';
import CopySuccessModal from './components/CopySuccessModal';
import SignatureDisplay from './components/SignatureDisplay';
import { ReactComponent as SingularisLogo } from './logo/Singularis_logo.svg';


const GOOGLE_FONTS = [
  'Inter',
  'Figtree',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Poppins',
  'Source Sans Pro',
  'Nunito',
  'Verdana',
  'Arial',
  'Georgia',
  'Tahoma',
  'Times New Roman',
  'Trebuchet MS'
];

const TEMPLATES = [
  { id: 'moderne', name: 'Moderne', description: 'Barre verticale colorée + photo ronde' },
  { id: 'moderne2', name: 'Moderne 2', description: 'Barre verticale entre photo et texte' },
  { id: 'moderne3', name: 'Moderne 3', description: 'Barre verticale à droite + icônes sociales verticales' },
  { id: 'carte', name: 'Carte', description: 'Fond gris, style carte de visite' },
  { id: 'minimal', name: 'Minimal', description: 'Tout en ligne, épuré' },
  { id: 'corporate', name: 'Corporate', description: 'Bordures, tableau structuré' },
  { id: 'creatif', name: 'Créatif', description: 'Dégradés, emojis, badges colorés' },
  { id: 'tech', name: 'Tech', description: 'Fond noir, style terminal' }
];

function App() {
  const [formData, setFormData] = useState({
    // Informations personnelles
    nom: '',
    poste: '',
    departement: '',
    entreprise: '',
    
    // Contact
    telephone: '',
    email: '',
    siteWeb: '',
    adresse: '',
    
    // Images
    photoUrl: '',
    banniereUrl: '',
    lienBanniere: '',
    photoSize: 100,
    bannerSize: 200,
    
    // Style
    police: 'Inter',
    taillePolice: 14,
    couleurNom: '#000000',
    couleurAccent: '#FF6B35',
    
    // Template
    template: 'moderne',
    
    // Réseaux sociaux
    showSocialIcons: false,
    social_linkedin: 'https://linkedin.com/in/votre-profil',
    
    // Personnalisation des icônes des réseaux sociaux
    iconSize: 21,
    iconShape: 'rond',
    iconColor: '#000000',
    iconBackgroundColor: '#000000',
    
    // Icônes de contact
    telephone_icon_enabled: false,
    email_icon_enabled: false,
    siteWeb_icon_enabled: false,
    adresse_icon_enabled: false,
    
    // Ordre des champs de contact
    contactOrder: [],
    
    // Ordre des réseaux sociaux
    socialOrder: [],
    
    // Boutons CTA
    ctaButtons: [],
    
    // Modules - Paramètres spécifiques aux templates
    // Modèle Moderne
    barrePosition: 'gauche',
    photoPosition: 'centrer',
    photoContour: false,
    photoFormat: 'rond',
    contactIconsEnabled: false,
    
    // Modèle Créatif
    creatif: {
      photoFormat: 'rond',
      photoPosition: 'droite',
      photoContour: true,
      emojisEnabled: true,
      socialPosition: 'horizontal',
      backgroundColor: '#FF6B35'
    },
    
    // Modèle Tech
    tech: {
      whoami: 'whoami',
      photoContour: true,
      photoFormat: 'carre'
    }
  });

  // Header component
  const Header = () => (
    <header className="app-header">
      <div className="header-content">
        <div className="logo-container">
          <SingularisLogo className="app-logo" />
        </div>
      </div>
    </header>
  );

  // États pour les modals
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyType, setCopyType] = useState('');


  // Sauvegarde automatique dans localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('emailSignatureData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        // Nettoyer les anciennes valeurs par défaut des réseaux sociaux
        const cleanedData = { ...parsed };
        
        // Supprimer les valeurs par défaut des réseaux sociaux si elles existent
        const defaultUrls = {
          'social_linkedin': 'https://linkedin.com/in/test',
          'social_facebook': 'https://facebook.com/test', 
          'social_twitter': 'https://x.com/test',
          'social_instagram': 'https://instagram.com/test',
          'social_tiktok': 'https://tiktok.com/@test',
          'social_twitch': 'https://twitch.tv/test'
        };
        
        Object.keys(defaultUrls).forEach(key => {
          if (cleanedData[key] === defaultUrls[key]) {
            delete cleanedData[key];
          }
        });
        
        // Nettoyer le socialOrder des valeurs par défaut
        if (cleanedData.socialOrder) {
          cleanedData.socialOrder = cleanedData.socialOrder.filter(item => {
            if (item.type === 'predefined' && item.data && item.data.url) {
              const defaultUrl = defaultUrls[`social_${item.key}`];
              return item.data.url !== defaultUrl;
            }
            return true;
          });
        }
        
        setFormData(prev => ({ ...prev, ...cleanedData }));
      } catch (error) {
        console.error('Erreur lors du chargement des données sauvegardées:', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('emailSignatureData', JSON.stringify(formData));
  }, [formData]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const convertImageUrl = (url) => {
    if (!url) return url;
    
    // Conversion des URLs Google Drive
    // Format: https://drive.google.com/file/d/FILE_ID/view?usp=sharing
    // Vers: https://drive.google.com/uc?export=view&id=FILE_ID
    const driveMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (driveMatch) {
      return `https://drive.google.com/uc?export=view&id=${driveMatch[1]}`;
    }
    
    // Conversion des URLs Google Photos
    // Format: https://photos.google.com/share/... ou https://photos.app.goo.gl/...
    // Note: Google Photos nécessite souvent une approche différente
    if (url.includes('photos.google.com') || url.includes('photos.app.goo.gl')) {
      // Pour Google Photos, on essaie d'extraire l'URL directe si possible
      // Sinon on retourne l'URL originale avec un avertissement
      console.warn('URL Google Photos détectée. Assurez-vous que l\'image est publique et accessible.');
      return url;
    }
    
    // Conversion des URLs Dropbox
    // Format: https://www.dropbox.com/s/FILE_ID/filename?dl=0
    // Vers: https://www.dropbox.com/s/FILE_ID/filename?raw=1
    if (url.includes('dropbox.com') && url.includes('?dl=0')) {
      return url.replace('?dl=0', '?raw=1');
    }
    
    return url;
  };



  const generateHTML = async () => {
    try {
      let signatureHTML;
      
      // Utiliser la fonction spécifique pour le modèle Moderne 2
      if (formData.template === 'moderne2') {
        signatureHTML = SignatureDisplay.generateHTMLForCopy(formData);
      } else {
        // Utiliser ReactDOMServer pour les autres templates
        signatureHTML = ReactDOMServer.renderToStaticMarkup(
          React.createElement(SignatureDisplay, { formData })
        );
      }
      
      // Créer un document HTML complet avec les styles nécessaires
      const fullHTML = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Signature Email</title>
    <style>
        body {
            margin: 0;
            padding: 20px;
            font-family: Arial, sans-serif;
            background-color: #f5f5f5;
        }
        .signature-container {
            display: inline-block;
            background-color: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
    </style>
</head>
<body>
    <div class="signature-container">
        ${signatureHTML}
    </div>
</body>
</html>`;
      
      return fullHTML;
    } catch (error) {
      console.error('Erreur lors de la génération du HTML:', error);
      return '<p>Erreur lors de la génération de l\'aperçu</p>';
    }
  };

  const handleCopySignature = async () => {
    try {
      const html = await generateHTML();
      
      // Copier en tant que HTML formaté pour Gmail et autres clients
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([html], { type: 'text/plain' })
      });
      
      await navigator.clipboard.write([clipboardItem]);
      setCopyType('signature');
      setCopyModalOpen(true);
    } catch (error) {
      console.error('Erreur lors de la copie de la signature:', error);
      // Fallback vers la méthode texte si ClipboardItem n'est pas supporté
      try {
        const html = await generateHTML();
        await navigator.clipboard.writeText(html);
        setCopyType('signature');
        setCopyModalOpen(true);
      } catch (fallbackError) {
        console.error('Erreur lors du fallback:', fallbackError);
        alert('Erreur lors de la copie. Veuillez réessayer.');
      }
    }
  };

  const handleCopySourceCode = async () => {
    try {
      const html = await generateHTML();
      await navigator.clipboard.writeText(html);
      setCopyType('source');
      setCopyModalOpen(true);
    } catch (error) {
      console.error('Erreur lors de la copie du code source:', error);
      alert('Erreur lors de la copie. Veuillez réessayer.');
    }
  };



  return (
    <div className="App">
      <Header />
      <MainContainer
        formData={formData}
        updateFormData={updateFormData}
        templates={TEMPLATES}
        googleFonts={GOOGLE_FONTS}
        convertImageUrl={convertImageUrl}
        onExport={handleCopySourceCode}
        onPreview={handleCopySignature}
      />
      
      <CopySuccessModal
        isOpen={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        copyType={copyType}
      />
      

    </div>
  );
}

export default App;